import { useState } from "react";
import {
  STATUS_CONTENT_CONFIG,
  STATUS_DISPLAY_CONFIG,
  STATUS_INFO_BOX_CONFIG
} from "../../constants/eddStatusConfig";
import { requestLimitUpgrade } from "../../handlers/requestLimitUpgrade";
import useEddStatus from "../../hooks/useEddStatus";
import {
  EddStatus,
  EddStatusType,
  UpgradeLimitState
} from "../../types/edd.types";
import Button from "../ui/Button/Button";
import ErrorState from "../ui/ErrorState/ErrorState";
import Loader from "../ui/Loader/Loader";
import MudrexIcon from "../ui/MudrexIcon/MudrexIcon";

const EddStatusView: React.FC<{ setShowForm: () => void }> = ({
  setShowForm
}) => {
  const { data: eddStatusData, isFetching, errors } = useEddStatus();
  const status = eddStatusData?.status;
  const [upgradeLimitState, setUpgradeLimitState] = useState<UpgradeLimitState>(
    UpgradeLimitState.IDLE
  );

  const contentConfig = STATUS_CONTENT_CONFIG[status as EddStatusType] ?? null;
  const displayConfig = STATUS_DISPLAY_CONFIG[status as EddStatusType] ?? null;
  const infoBoxConfig = STATUS_INFO_BOX_CONFIG[status as EddStatusType] ?? null;

  const handleActionButtonClick = async () => {
    if (
      status === EddStatus.RESUBMISSION_REQUESTED ||
      status === EddStatus.INITIATED
    ) {
      return setShowForm();
    }

    if (status === EddStatus.UNINITIATED || status === EddStatus.APPROVED) {
      setUpgradeLimitState(UpgradeLimitState.LOADING);
      try {
        const response = await requestLimitUpgrade();
        if (response?.success) setShowForm();
      } catch (error) {
        setUpgradeLimitState(UpgradeLimitState.ERROR);
      } finally {
        setUpgradeLimitState(UpgradeLimitState.IDLE);
      }
    }
  };

  // Show loader while data is fetching or upgrading limit
  if (isFetching || upgradeLimitState !== UpgradeLimitState.IDLE) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader />
      </div>
    );
  }

  // If there are errors or no data after loading, show error state
  if (errors || !contentConfig || !displayConfig) {
    return (
      <ErrorState
        title="Unable to Load Status"
        description="Please try again later."
      />
    );
  }

  return (
    <div className="flex flex-col items-center justify-center min-h-screen p-4 gap-6">
      <header className="text-center">
        <h2 className="text-2xl font-bold text-gray-900 mb-2">
          {contentConfig.headingText}
        </h2>
        <p className="text-gray-600">{contentConfig.subheadingText}</p>
      </header>

      {/* Status Card */}
      <div className="w-full bg-[#F5F3FF] rounded-xl p-4 flex items-center gap-4">
        <MudrexIcon icon="FileText" size={40} color="#8B7AB8" />
        <div>
          <h3 className="text-gray-900 mb-1">Due Diligence</h3>
          <div className="flex items-center gap-1.5 text-sm">
            <MudrexIcon
              icon={displayConfig?.iconName}
              size={16}
              color={displayConfig?.accentColor}
            />
            <p
              className="text-sm"
              style={{ color: displayConfig?.accentColor }}
            >
              {displayConfig?.statusLabel}
            </p>
          </div>
        </div>
      </div>

      {/* Helper text for uninitiated */}
      {status === EddStatus.UNINITIATED && (
        <p className="text-xs text-gray-500 text-center">
          Takes few mins to fill. Progress Autosaved
        </p>
      )}

      {/* Info Box */}
      {eddStatusData?.info && infoBoxConfig && (
        <div
          className={`w-full rounded-xl p-5 flex items-center gap-4 ${infoBoxConfig.bg} shadow-sm`}
        >
          <div className="flex-shrink-0">
            <MudrexIcon
              icon={infoBoxConfig.icon}
              size={24}
              color={infoBoxConfig.iconColor}
            />
          </div>
          <p
            className={`text-sm font-medium flex-1 leading-6 ${infoBoxConfig.textColor}`}
          >
            {eddStatusData.info}
          </p>
        </div>
      )}

      {/* CTA Button */}
      {contentConfig.actionButtonText && (
        <Button
          type="primary"
          className="w-full"
          onClick={handleActionButtonClick}
        >
          {contentConfig.actionButtonText}
        </Button>
      )}
    </div>
  );
};

export default EddStatusView;
